import { decodePrettyUrl } from '@src/utils/performance-utils';

// Page/table presets ride in the query string: `/<channel>/<tab>?preset=<Name>&table=<Name>`.
const PAGE_PRESET_PARAM = 'preset';
const TABLE_PRESET_PARAM = 'table';

// The legacy path-based form is still parsed so old bookmarks and shared links keep working.
// bpm-frontend produced `/<channel>/<tab>_page-preset=<Name>_table-preset=<Name>` (tab and preset
// in one segment; see ChannelLegacyPresetRedirect), and hand-edited links add a slash or drop the
// tab.
const LEGACY_PAGE_PRESET_PREFIX = '_page-preset=';
const LEGACY_TABLE_PRESET_PREFIX = '_table-preset=';
const LEGACY_PRESET_PATH_REGEX = /\/_(page|table)-preset=/;

export interface PresetNames {
  pagePresetName?: string;
  tablePresetName?: string;
}

/**
 * Parses a legacy preset suffix (`_page-preset=A_table-preset=B`, `_table-preset=B` alone, with or
 * without a leading slash). Returns undefined names for whatever is absent.
 */
export const parseLegacyPresetSuffix = (suffix: string): PresetNames => {
  const trimmed = suffix.replace(/^\/+/, '');
  const tableIndex = trimmed.indexOf(LEGACY_TABLE_PRESET_PREFIX);
  const pageName = trimmed.startsWith(LEGACY_PAGE_PRESET_PREFIX)
    ? trimmed.slice(LEGACY_PAGE_PRESET_PREFIX.length, tableIndex >= 0 ? tableIndex : trimmed.length)
    : '';
  const tableName =
    tableIndex >= 0 ? trimmed.slice(tableIndex + LEGACY_TABLE_PRESET_PREFIX.length) : '';
  return {
    pagePresetName: pageName ? decodePrettyUrl(pageName) : undefined,
    tablePresetName: tableName ? decodePrettyUrl(tableName) : undefined
  };
};

export const hasLegacyPresetSuffix = (suffix: string): boolean =>
  suffix.startsWith(LEGACY_PAGE_PRESET_PREFIX) || suffix.startsWith(LEGACY_TABLE_PRESET_PREFIX);

/** `search` (e.g. `location.search`) with the preset params set, or removed when not given. */
export const setPresetSearchParams = (
  search: string,
  { pagePresetName, tablePresetName }: PresetNames
): string => {
  const params = new URLSearchParams(search);
  params.delete(PAGE_PRESET_PARAM);
  params.delete(TABLE_PRESET_PARAM);
  if (pagePresetName) {
    params.set(PAGE_PRESET_PARAM, pagePresetName);
  }
  if (tablePresetName) {
    params.set(TABLE_PRESET_PARAM, tablePresetName);
  }
  const result = params.toString();
  return result ? `?${result}` : '';
};

/** Preset names from the query string, falling back to a legacy path suffix. */
export const getPresetNamesFromUrl = (search: string, legacySuffix: string): PresetNames => {
  const params = new URLSearchParams(search);
  const legacy = parseLegacyPresetSuffix(legacySuffix);
  return {
    pagePresetName: params.get(PAGE_PRESET_PARAM) || legacy.pagePresetName,
    tablePresetName: params.get(TABLE_PRESET_PARAM) || legacy.tablePresetName
  };
};

/** Pathname without a legacy `/_page-preset=…` suffix, or the same pathname if it has none. */
export const stripLegacyPresetPath = (pathname: string): string => {
  const index = pathname.search(LEGACY_PRESET_PATH_REGEX);
  return index >= 0 ? pathname.slice(0, index) : pathname;
};
